import React, { useEffect, useMemo, useState } from "react";
import { ResponsiveRadar } from "@nivo/radar";
import {
  CA100_disclosureMetrics,
  FormattedCA100Metric,
} from "./CA100Constants";
import { PublicMetric } from "../../../models/CompanyprofileModels";
import { buildCADisclosureData } from "./util";
import {
  compassBordeaux,
  compassLightGreen,
  compassYellow,
  lightGreyText,
} from "../../../styles";

enum RadarAreaKey {
  CA100Disclosure = "CA100Disclosure",
}

enum ChartIndexBy {
  Indicator = "indicator",
}

enum IndicatorValueScore {
  Y = 2,
  Partial = 1,
  N = 0,
  "-" = 0,
}

export enum ScoreTier {
  Bad = "Bad",
  Mid = "Mid",
  Good = "Good",
}

type NivoRadarDatum = Record<string, unknown>;

interface RadarDatum extends NivoRadarDatum {
  indicator: string;
  [RadarAreaKey.CA100Disclosure]: number;
}

const MAX_SCORE = 22;

const TIER_THRESHOLDS = {
  [ScoreTier.Bad]: 0,
  [ScoreTier.Mid]: MAX_SCORE / 4,
  [ScoreTier.Good]: MAX_SCORE / 2,
};

export const TierColor: Record<ScoreTier, string> = {
  [ScoreTier.Bad]: compassBordeaux,
  [ScoreTier.Mid]: compassYellow,
  [ScoreTier.Good]: compassLightGreen,
};

export const getScoreTier = (score: number): ScoreTier => {
  if (score < TIER_THRESHOLDS[ScoreTier.Mid]) return ScoreTier.Bad;
  if (score < TIER_THRESHOLDS[ScoreTier.Good]) return ScoreTier.Mid;
  return ScoreTier.Good;
};

export const getScoreColor = (score: number): string =>
  TierColor[getScoreTier(score)];

const transformToRadarData = (
  metricsData: PublicMetric[],
  area: RadarAreaKey = RadarAreaKey.CA100Disclosure
): RadarDatum[] => {
  const indicators: FormattedCA100Metric<CA100_disclosureMetrics>[] =
    buildCADisclosureData(metricsData);
  return indicators.map((indicator) => ({
    indicator: indicator.metricName,
    [area]:
      IndicatorValueScore[
        indicator.value as keyof typeof IndicatorValueScore
      ] || IndicatorValueScore.N,
  }));
};

export const CA100RadarChart = ({
  metricsData,
}: {
  metricsData: PublicMetric[];
}): JSX.Element => {
  const [data, setData] = useState<RadarDatum[]>([]);
  const totalScore = useMemo(() => {
    return data.reduce(
      (acc, datum) => acc + (datum[RadarAreaKey.CA100Disclosure] as number),
      0
    );
  }, [data]);

  const chartColor = useMemo(() => getScoreColor(totalScore), [totalScore]);

  useEffect(() => {
    setData(transformToRadarData(metricsData));
  }, [metricsData]);
  return (
    <ResponsiveRadar<RadarDatum>
      data={data}
      keys={[RadarAreaKey.CA100Disclosure]}
      indexBy={ChartIndexBy.Indicator}
      maxValue={2}
      gridLevels={2}
      valueFormat=">-.2f"
      margin={{ top: 15, right: 124, bottom: 15, left: 10 }}
      borderColor={chartColor}
      gridLabelOffset={8}
      enableDots={false}
      dotColor={{ theme: "background" }}
      dotBorderWidth={2}
      colors={[chartColor]}
      blendMode="multiply"
      motionConfig="wobbly"
      sliceTooltip={(): null => null}
      legends={[]}
      theme={{
        axis: {
          ticks: {
            text: {
              fontSize: 12,
              fill: lightGreyText,
              fontFamily: "Apercu, Inter, sans-serif",
            },
          },
        },
      }}
    />
  );
};
